import React, { useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Skeleton,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { fetchCeoMetricDetail } from '../services/ceoReportingService';
import { buildCeoDashboardUrl, parseCeoPeriod } from './ceo/ceoMetricRoutes';

const audFmt = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  maximumFractionDigits: 0,
});

const dashboardCardSx = {
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
  borderRadius: 3,
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
};

function sfLinkColumn() {
  return {
    field: 'salesforceUrl',
    headerName: 'SF',
    width: 72,
    sortable: false,
    renderCell: (params) =>
      params.row.salesforceUrl ? (
        <Link href={params.row.salesforceUrl} target="_blank" rel="noopener noreferrer">
          <OpenInNewIcon fontSize="small" />
        </Link>
      ) : null,
  };
}

function wpLinkColumn() {
  return {
    field: 'link',
    headerName: 'View',
    width: 72,
    sortable: false,
    renderCell: (params) =>
      params.row.link ? (
        <Link href={params.row.link} target="_blank" rel="noopener noreferrer">
          <OpenInNewIcon fontSize="small" />
        </Link>
      ) : null,
  };
}

export default function CeoMetricDetailPage() {
  const navigate = useNavigate();
  const { metric } = useParams();
  const [searchParams] = useSearchParams();
  const period = parseCeoPeriod(searchParams.get('period'));
  const windowRole = searchParams.get('window') || 'current';
  const stage = searchParams.get('stage') || undefined;
  const group = searchParams.get('group') ?? undefined;

  const detailQuery = useQuery({
    queryKey: ['ceoDetail', metric, period, windowRole, stage, group],
    queryFn: () => fetchCeoMetricDetail({ metric, period, window: windowRole, stage, group }),
    enabled: Boolean(metric),
  });

  const gridColumns = useMemo(() => {
    const base = detailQuery.data?.columns || [];
    return base.map((col) => {
      if (col.field === 'salesforceUrl') return sfLinkColumn();
      if (col.field === 'link') return wpLinkColumn();
      if (col.field === 'revenue') {
        return {
          ...col,
          valueFormatter: (v) => (v == null || v === '' ? '—' : audFmt.format(Number(v))),
        };
      }
      return col;
    });
  }, [detailQuery.data?.columns]);

  const rows = useMemo(
    () => (detailQuery.data?.rows || []).map((r) => ({ ...r })),
    [detailQuery.data?.rows]
  );

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(buildCeoDashboardUrl(period))}
        sx={{ mb: 2 }}
      >
        Back to CEO reporting
      </Button>

      <Card sx={dashboardCardSx}>
        <CardContent>
          {detailQuery.isLoading ? (
            <>
              <Skeleton width={280} height={32} />
              <Skeleton width="100%" height={48} sx={{ mt: 1 }} />
              <Skeleton variant="rectangular" height={400} sx={{ mt: 2 }} />
            </>
          ) : detailQuery.isError ? (
            <Alert severity="error">
              {detailQuery.error?.response?.data?.details ||
                detailQuery.error?.message ||
                'Failed to load detail.'}
            </Alert>
          ) : (
            <>
              <Typography variant="h5" fontWeight={600} gutterBottom>
                {detailQuery.data?.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {detailQuery.data?.description}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {detailQuery.data?.periodLabel} · {detailQuery.data?.totalCount} record
                {detailQuery.data?.totalCount === 1 ? '' : 's'}
                {detailQuery.data?.truncated
                  ? ` (showing first ${rows.length} — refine filters or export from Salesforce)`
                  : ''}
              </Typography>
              {rows.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                  No records for this selection.
                </Typography>
              ) : (
                <DataGridPro
                  rows={rows}
                  columns={gridColumns}
                  autoHeight
                  hideFooter={rows.length <= 25}
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  disableRowSelectionOnClick
                  density="comfortable"
                  sx={{ border: 'none' }}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
